import { gx, GTAX_HOST } from "@/components/gtax/base";
import { useEffect } from "react";
import { Link, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/components/gtax/gtax.css";
import { Nav, Footer } from "@/components/gtax/GtaxShell";
import { SupportChat } from "@/components/gtax/SupportChat";
import { GtaxHero } from "@/components/gtax/GtaxHero";
import { GtaxPerformance } from "@/components/gtax/GtaxPerformance";
import { GtaxHow, GtaxRoadmap } from "@/components/gtax/GtaxHow";
import { GtaxTiers } from "@/components/gtax/GtaxTiers";
import { GtaxTraded } from "@/components/gtax/GtaxTraded";
import GtaxAbout from "@/components/gtax/pages/About";
import GtaxStrategy from "@/components/gtax/pages/Strategy";
import GtaxReport from "@/components/gtax/pages/Report";
import GtaxAccess from "@/components/gtax/pages/Access";
import GtaxLegal from "@/components/gtax/pages/Legal";
import GtaxContact from "@/components/gtax/pages/Contact";
import GtaxStatus from "@/components/gtax/pages/Status";
import GtaxInstall from "@/components/gtax/pages/Install";
import GtaxAdmin from "@/components/gtax/pages/Admin";
import { PageHead } from "@/components/gtax/GtaxShell";
import { GtaxGoldChart } from "@/components/gtax/GtaxGoldChart";
import { Facts, Faq, Prose, Steps } from "@/components/gtax/GtaxSections";

const tgApp = () => {
  const w = window.Telegram?.WebApp;
  if (!w?.initData) return;
  w.ready();
  w.expand();
  w.setHeaderColor?.("#0A0907");
  w.setBackgroundColor?.("#0A0907");
};

const Cta = () => (
  <section className="py-32 gx-line text-center relative overflow-hidden" data-testid="gtax-access">
    <img src="/gtax/vaultdoor.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-[.12]" />
    <div className="gx-wrap relative">
      <div className="mt-8 h-px w-24 mx-auto bg-[color:var(--gold)]" />
      <h2 className="gx-serif text-5xl md:text-6xl mt-10">By invitation, for now.</h2>
      <p className="mt-6 text-[color:var(--mute)] max-w-xl mx-auto text-lg">GTA X opens first to a private circle of members. Sign the digital agreement, verify your identity and settle your allocation.</p>
      <Link to={gx("/access")} className="gx-btn mt-12" data-testid="gtax-access-cta">Request access</Link>
    </div>
  </section>
);

const Home = () => (
  <>
    <GtaxHero />
    <GtaxGoldChart />
    <GtaxPerformance />
    <GtaxHow />
    <Facts />
    <GtaxTraded />
    <GtaxTiers />
    <GtaxRoadmap />
    <Cta />
  </>
);

const Membership = () => (
  <>
    <PageHead eyebrow="Membership" title="Three tiers. One mandate. Clear terms for every member."
      sub="Every tier participates in the same consolidated mandate, under the same risk limits and the same written agreement. Tiers differ only in minimum allocation, profit-share ratio, reporting frequency and redemption schedule." img="/gtax/bars.jpg" />
    <GtaxTiers />
    <Steps id="join" eyebrow="How membership works" title="Six steps, each recorded, from invitation to your first statement." steps={[
      ["Invitation", "Membership is by invitation from an existing member, through a personal link on the website or in the official GTA X Telegram bot."],
      ["Your details", "You provide your name, contact details, country of residence, chosen tier and the wallet where redemptions should be paid."],
      ["The agreement", "You read the full Membership Agreement, confirm each declaration and sign digitally. A PDF copy of the signed agreement is available to you immediately."],
      ["Approval", "We review your membership and may verify your identity and the source of your funds. Treasury details are released only after approval."],
      ["Settlement", "You send USDT or USDC from your own wallet, on the network you selected, and submit the transaction ID. We verify it on-chain before crediting your ledger."],
      ["Reporting", "Your allocation participates from the next trading day. Balances, statements and redemption requests are available on the website and in Telegram."],
    ]} />
    <Prose id="fees" eyebrow="Costs and profit share" title="What members pay, stated plainly." ivory paras={[
      "Standard membership carries no licence fee. VIP I, VIP II and VIP III carry an annual membership licence, payable in advance, in exchange for a higher member share of realised profit, more frequent reporting and faster redemptions.",
      "The profit-share ratio applies only to realised net trading profit. If the mandate does not realise a profit in a period, no profit share is taken by the Association and no distribution is made to members. Losses are shared pro-rata and reduce ledger balances.",
      "Introducer rewards, where they apply, are paid only from realised profit. They are never deducted from any member's allocation or balance.",
    ]} />
    <Faq items={[
      ["Is any return guaranteed?", "No. GTA X does not offer, promise or target any fixed, minimum or periodic return. Distributions are paid only from realised net trading profit and losses are shared pro-rata. Past performance doesn't guarantee future results."],
      ["Who executes the trades?", "Orders are executed through VT Markets Limited, regulated by the Financial Services Commission of Mauritius (licence GB23202269). The broker's licence covers the broker only, not GTA X."],
      ["How do I join?", "Membership is by invitation. An existing member can share their personal invitation link with you from their member status page or from the GTA X Telegram bot."],
      ["How are deposits and redemptions made?", "In USDT or USDC, on the network you choose. Deposits go only to the treasury address shown to you privately after approval. Redemptions are paid only to the wallet you registered, on your tier's schedule."],
      ["Can I leave at any time?", "Yes. You may end your membership by written notice. Your available balance, after settlement of any open positions, is returned to your registered redemption wallet."],
      ["Which law governs the agreement?", "The Membership Agreement is governed by the laws applicable in the Abu Dhabi Global Market, with disputes referred to the ADGM Courts."],
    ]} />
    <Cta />
  </>
);

export default function GtaxPage() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  useEffect(() => {
    document.title = "GTA X — Gold Trading Association";
    tgApp();
    if (GTAX_HOST) document.querySelectorAll("link[rel~='icon']").forEach((l) => { l.href = "/gtax/logo.png"; });
  }, []);
  return (
    <div className="gx t-champagne" id="top" data-testid="gtax-page">
      <Toaster position="top-center" />
      <Nav />
      <Routes>
        <Route index element={<Home />} />
        <Route path="about" element={<GtaxAbout />} />
        <Route path="app" element={<GtaxInstall />} />
        <Route path="strategy" element={<GtaxStrategy />} />
        <Route path="report" element={<GtaxReport />} />
        <Route path="membership" element={<Membership />} />
        <Route path="access" element={<GtaxAccess />} />
        <Route path="contact" element={<GtaxContact />} />
        <Route path="status" element={<GtaxStatus />} />
        <Route path="admin" element={<GtaxAdmin />} />
        {["disclaimer", "terms", "privacy", "risk"].map((d) => <Route key={d} path={d} element={<GtaxLegal doc={d} />} />)}
        <Route path="*" element={<Home />} />
      </Routes>
      <Footer />
      <SupportChat />
    </div>
  );
}
